import type { Vec2 } from "../game/math";
import { sampleTrail } from "../game/trail";
import type { CosmeticPattern } from "../theme/theme";

export type CosmeticDetailLevel = "quality" | "reduced";

export type CosmeticHeadMarkKind =
  | "plain-line"
  | "signal-arrow"
  | "ember-core"
  | "tide-node"
  | "circuit-node"
  | "constellation-prism";

export type CosmeticSurfaceDetailKind = "none" | "tide-ripple";

export type CosmeticMarkKind =
  | "signal-chevron"
  | "signal-node"
  | "ember-spot"
  | "tide-node"
  | "circuit-branch"
  | "circuit-node"
  | "constellation-star"
  | "prism-facet";

export interface CosmeticSurfaceSample {
  readonly center: Vec2;
  readonly angle: number;
  readonly radius: number;
  readonly index: number;
}

export interface CosmeticMark {
  readonly center: Vec2;
  readonly angle: number;
  readonly radius: number;
  readonly side: -1 | 0 | 1;
  readonly kind: CosmeticMarkKind;
}

export interface CosmeticGeometry {
  readonly silhouetteWidth: number;
  readonly samples: readonly CosmeticSurfaceSample[];
  readonly marks: readonly CosmeticMark[];
  readonly surfaceDetail: CosmeticSurfaceDetailKind;
  readonly head: {
    readonly center: Vec2;
    readonly angle: number;
    readonly radius: number;
    readonly mark: CosmeticHeadMarkKind;
  };
}

export interface CosmeticHeadPose {
  readonly center: Vec2;
  readonly angle: number;
}

const headMarkForPattern = (pattern: CosmeticPattern): CosmeticHeadMarkKind => {
  if (pattern === "signal") return "signal-arrow";
  if (pattern === "ember") return "ember-core";
  if (pattern === "tide") return "tide-node";
  if (pattern === "circuit") return "circuit-node";
  if (pattern === "constellation") return "constellation-prism";
  return "plain-line";
};

const angleBetween = (from: Vec2, to: Vec2): number =>
  Math.atan2(to.y - from.y, to.x - from.x);

const sampleAngle = (
  samples: readonly Vec2[],
  index: number,
  fallback: number,
): number => {
  const towardHead = samples[Math.max(0, index - 1)];
  const towardTail = samples[Math.min(samples.length - 1, index + 1)];
  return towardHead && towardTail
    ? angleBetween(towardTail, towardHead)
    : fallback;
};

const mark = (
  sample: CosmeticSurfaceSample,
  kind: CosmeticMarkKind,
  radius: number,
  side: -1 | 0 | 1 = 0,
): CosmeticMark => ({
  center: sample.center,
  angle: sample.angle,
  radius,
  side,
  kind,
});

const buildMarks = (
  samples: readonly CosmeticSurfaceSample[],
  width: number,
  pattern: CosmeticPattern,
  detailLevel: CosmeticDetailLevel,
): CosmeticMark[] => {
  if (pattern === "plain") return [];
  const sparse = detailLevel === "reduced" ? 2 : 1;
  const marks: CosmeticMark[] = [];

  for (let index = 0; index < samples.length; index += 1) {
    const sample = samples[index];
    if (!sample) continue;
    if (pattern === "signal") {
      if (index % (2 * sparse) === 0)
        marks.push(mark(sample, "signal-chevron", width * 0.22));
      if (index % (5 * sparse) === 0)
        marks.push(mark(sample, "signal-node", width * 0.11));
    } else if (pattern === "ember") {
      if (index % (2 * sparse) === 0) {
        marks.push(
          mark(
            sample,
            "ember-spot",
            width * (index % 4 === 0 ? 0.13 : 0.09),
            index % 4 === 0 ? -1 : 1,
          ),
        );
      }
    } else if (pattern === "tide") {
      if (index % (3 * sparse) === 0)
        marks.push(mark(sample, "tide-node", width * 0.16));
    } else if (pattern === "circuit") {
      if (index % (2 * sparse) === 0)
        marks.push(
          mark(sample, "circuit-branch", width * 0.2, index % 4 === 0 ? -1 : 1),
        );
      if (index % (5 * sparse) === 0)
        marks.push(mark(sample, "circuit-node", width * 0.09));
    } else if (pattern === "constellation") {
      if (index % (3 * sparse) === 0)
        marks.push(
          mark(
            sample,
            "constellation-star",
            width * 0.12,
            index % 2 === 0 ? -1 : 1,
          ),
        );
      if (index === 0 || index === Math.max(0, samples.length - 2 * sparse)) {
        marks.push(mark(sample, "prism-facet", width * 0.25));
      }
    }
  }
  return marks;
};

export const buildCosmeticGeometry = (
  trailPoints: readonly Vec2[],
  width: number,
  pattern: CosmeticPattern,
  detailLevel: CosmeticDetailLevel,
  headPose?: CosmeticHeadPose,
): CosmeticGeometry => {
  const safeWidth = Number.isFinite(width) && width > 0 ? width : 1;
  const fallback = trailPoints.at(-1) ?? { x: 0, y: 0 };
  const spacing = safeWidth * (detailLevel === "quality" ? 0.72 : 1.18);
  const trailSamples = sampleTrail({ points: trailPoints }, spacing);
  const head = trailSamples[0] ?? fallback;
  const headNeighbor = trailSamples[1] ?? trailPoints.at(-2) ?? head;
  const headAngle = angleBetween(headNeighbor, head);
  const radius = safeWidth / 2;
  const samples: CosmeticSurfaceSample[] = [];
  const maximumSamples = detailLevel === "quality" ? 32 : 18;
  const sampleStride = Math.max(
    1,
    Math.ceil(Math.max(0, trailSamples.length - 1) / maximumSamples),
  );
  const appendSample = (sampleIndex: number): void => {
    const center = trailSamples[sampleIndex];
    if (!center) return;
    samples.push({
      center,
      angle: sampleAngle(trailSamples, sampleIndex, headAngle),
      radius,
      index: samples.length,
    });
  };
  for (
    let sampleIndex = 1;
    sampleIndex < trailSamples.length;
    sampleIndex += sampleStride
  ) {
    appendSample(sampleIndex);
  }
  const tailSampleIndex = trailSamples.length - 1;
  if (
    tailSampleIndex > 0 &&
    samples.at(-1)?.center !== trailSamples[tailSampleIndex]
  ) {
    if (samples.length < maximumSamples) appendSample(tailSampleIndex);
    else {
      const center = trailSamples[tailSampleIndex];
      if (center)
        samples[samples.length - 1] = {
          center,
          angle: sampleAngle(trailSamples, tailSampleIndex, headAngle),
          radius,
          index: samples.length - 1,
        };
    }
  }

  return {
    silhouetteWidth: safeWidth,
    samples,
    marks: buildMarks(samples, safeWidth, pattern, detailLevel),
    surfaceDetail:
      pattern === "tide" && detailLevel === "quality" ? "tide-ripple" : "none",
    head: {
      center: headPose?.center ?? head,
      angle: headPose?.angle ?? headAngle,
      radius,
      mark: headMarkForPattern(pattern),
    },
  };
};
